import { useEffect, useRef, useState } from 'react';
import { Box, Dialog, InputAdornment, TextField, Typography, useTheme } from '@mui/material';

import CommonButton from '../CommonButton';
import CommonModal from './CommonModal';
import Loading from '@/component/mui/Backdrop';
import { useWeb3 } from '@/hooks/use-web3';
import { COIN_NAME_TRI, POLYGON_AMOY, POLYGON_TRI, STAKKINGTOKEN } from '@/lib/common/name-space';
import { toast } from 'react-toastify';
import SymbolSvg from '@/assets/assetfi/Symbol';
import CloseSvg from '@/assets/assetfi/Close';
import { isCompleteModalType, ResGetPoolDetailsCall } from '@/types';
import BigNumber from 'bignumber.js';
import { useNavigate } from 'react-router-dom';
import UsdtBalance from '@/component/balance/UsdtBalance';
import { removeZeroStart } from '@/lib/util';
import { setTicker } from '@/component/balance/ticker';
import { setCurrencyTicker } from '@/component/admin/currency';

interface StakingModalPropsType {
  handleClose: () => void;
  open: boolean;
  contractAddr: string;
  detail: ResGetPoolDetailsCall;
  totalAmt: string;
}

const StakingModal = ({ handleClose, open, contractAddr, detail, totalAmt }: StakingModalPropsType) => {
  // theme
  const theme = useTheme();
  const { web3authCall } = useWeb3();

  // state
  const [stake, setStake] = useState('0');
  const [usdt, setUsdt] = useState('0');
  const [isCompleteModal, setIsCompleteModal] = useState<isCompleteModalType>({
    success: false,
    fail: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUpperMinAmount, setIsUpperMinAmount] = useState(false);
  const address = useRef<string>('');
  const navigate = useNavigate();
  useEffect(() => {
    if (address.current) {
      return;
    }
    (async () => {
      if (web3authCall?.connected()) {
        const addr = await web3authCall.getAddress();
        address.current = addr;
      }
    })();
    return () => {};
  }, [web3authCall]);
  useEffect(() => {
    (async () => {
      if (open) {
        console.log(`open`);
        setUsdt(await setTicker({ navigate: navigate }));
      }
    })();

    return () => {};
  }, [open]);

  // method
  const handleConfirm = async () => {
    if (!stake) {
      return;
    }
    if (!address.current) {
      return;
    }
    const usdtStake = BigNumber(stake).multipliedBy(usdt).toFixed();
    console.log(usdtStake);
    // 추가 예외처리
    if (BigNumber(detail.minStakePrice).gt(usdtStake)) {
      toast.error('Please check the minimum staking amount.');
      return;
    }
    if (BigNumber(totalAmt).plus(stake).gt(detail.maxFundraisingPrice)) {
      toast.error('The maximum fundraising amount of the pool has been exceeded, so staking is not possible.');
      return;
    }
    handleClose();
    setIsLoading(true);
    const adminCode = await setCurrencyTicker(web3authCall!);
    if (adminCode.code !== 200) {
      setIsLoading(false);
      toast.error('Update failed');
      return;
    }
    const code = await actionStake();
    setIsLoading(false);
    if (code !== 200) {
      toast.error('Transmission failed.');
    } else {
      toast.success('success');
    }
    setIsLoading(false);
  };
  const triggerIsCompleteModal = (key: keyof isCompleteModalType) => {
    setIsCompleteModal((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  const actionStake = async (): Promise<number> => {
    const payload = [address.current, stake];
    const { code } = await web3authCall!.callStaking(STAKKINGTOKEN, contractAddr, payload);
    return code;
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '320px',
            padding: '24px 0px',
            borderRadius: '20px',
          },
        }}
        open={open}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '0px 24px',
          }}
        >
          <SymbolSvg width={24} height={24} />
          <Typography color={theme.greyscale.darkgrey2} variant="h6">
            RWA Staking Hupayx No.1
          </Typography>

          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <CloseSvg width={24} height={24} />
          </Box>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px', position: 'relative' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Stake Amount
          </Typography>

          <Typography
            sx={{ position: 'absolute', right: 24, bottom: 28 }}
            color={theme.greyscale.grey3}
            variant="body2"
          >
            {BigNumber(stake).multipliedBy(usdt).toFixed()} USD
          </Typography>
          <TextField
            value={stake}
            placeholder="0"
            autoFocus
            onChange={(e) => {
              if (!e.target.value) {
                setStake('0');
                setIsUpperMinAmount(false);
                return;
              }
              setStake(removeZeroStart(e.target.value));
              const amt = BigNumber(e.target.value).multipliedBy(usdt).toFixed();
              if (!isUpperMinAmount && BigNumber(amt).gt(detail.minStakePrice)) {
                setIsUpperMinAmount(true);
              } else {
                setIsUpperMinAmount(false);
              }
            }}
            sx={{
              width: '100%',
              marginTop: '8px',

              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '& input': {
                  textAlign: 'right',
                  typography: 'h4',
                  color: theme.greyscale.darkgrey2,
                  padding: '8px',
                  paddingRight: '0px',
                  paddingBottom: '28px',
                },
                '& fieldset': {
                  borderColor: theme.background.blue,
                },
                '&:hover fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.background.blue,
                },
                '&.MuiInputBase-root': {
                  background: '#357AFF1A',
                  padding: 0,
                },
                '& .MuiTypography-root': {
                  typography: 'h5',
                  color: theme.greyscale.darkgrey2,
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment
                  sx={{
                    fontSize: '24px',
                    paddingRight: '8px',
                    paddingBottom: '20px',
                  }}
                  position="end"
                >
                  SFL
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={{ marginRight: '8px' }} align="right" variant="subtitle2" color={theme.greyscale.grey3}>
            Balance ~ <UsdtBalance symbol={COIN_NAME_TRI} chainId={POLYGON_AMOY} token={POLYGON_TRI} />
          </Typography>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          <Typography sx={{ marginLeft: '8px' }} variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Stake Amount
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {stake}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    SFL
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  ~ {BigNumber(stake).multipliedBy(usdt).toFixed()}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                APR
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.annualInterestMultipliedRate}%
                </Typography>
              </Box>
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Duration
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  1 Year
                </Typography>
              </Box>
            </Box> */}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
                borderBottom: `0.5px solid ${theme.greyscale.lightgrey2}`,
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Minimum Deposit
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  {detail.minStakePrice}{' '}
                  <Typography display={'inline'} variant="body4" color={theme.greyscale.darkgrey2}>
                    USD
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px',
              }}
            >
              <Typography variant="body2" color={theme.greyscale.grey1}>
                Total Rewards
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color={theme.greyscale.darkgrey2}>
                  0 USD
                </Typography>
              </Box>
            </Box> */}
          </Box>
        </Box>

        <Box sx={{ marginTop: '24px', padding: '0px 16px' }}>
          {isUpperMinAmount ? (
            <CommonButton
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={stake ? '#ffffff' : theme.background.blue}
              bgColor={stake ? theme.background.blue : '#ffffff'}
              border={theme.background.blue}
            />
          ) : (
            <CommonButton
              disabled
              sx={{ width: '100%', padding: '12px 20px', borderRadius: '10px' }}
              text="Confirm"
              handleClick={handleConfirm}
              variant="h6"
              color={theme.greyscale.lightgrey2}
              bgColor={theme.greyscale.lightgrey2}
              border={'#ffffff'}
            />
          )}
          <CommonButton
            sx={{
              width: '100%',
              padding: '12px 20px',
              borderRadius: '10px',
              marginTop: '10px',
            }}
            text="Back"
            handleClick={handleClose}
            variant="h6"
            color={theme.greyscale.lightgrey2}
            border={theme.greyscale.lightgrey2}
            bgColor={'#ffffff'}
          />
        </Box>
      </Dialog>

      {/*  Confirm 성공 시 */}
      <CommonModal
        open={isCompleteModal.success}
        handleClose={() => {
          triggerIsCompleteModal('success');
        }}
        title="Staking Complete"
        type="success"
      >
        <Typography color={theme.greyscale.grey2} variant="h6">
          Staking Completed Successfully.
          <br />
          Thank You.
        </Typography>
      </CommonModal>

      <Loading isLoading={isLoading} />

      {/*  Confirm 실패 시 */}
      <CommonModal
        open={isCompleteModal.fail}
        handleClose={() => {
          triggerIsCompleteModal('fail');
        }}
        title="Error"
        type="error"
      >
        <Typography color={theme.greyscale.grey2} variant="h6">
          Staking Fail.
        </Typography>
      </CommonModal>
    </>
  );
};

export default StakingModal;
